import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  X,
  Search,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
  Settings2,
  Clock,
  ChevronDown,
} from "lucide-react";
import { INTEGRATIONS, CATEGORIES, categoryLabel, searchIntegrations } from "@/data/integrations-catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  api: { label: "Available via API", cls: "bg-[#E7F6EE] text-[#067647]", Icon: CheckCircle2 },
  oauth: { label: "OAuth Connection", cls: "bg-[#E7F6EE] text-[#067647]", Icon: KeyRound },
  custom: { label: "Custom API", cls: "bg-[#EAF1FF] text-[#2563EB]", Icon: Settings2 },
  soon: { label: "Coming Soon", cls: "bg-amber-50 text-amber-600", Icon: Clock },
};

export const StatusPill = ({ type }) => {
  const s = STATUS[type];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${s.cls}`}>
      <s.Icon size={12} strokeWidth={2.2} />
      {s.label}
    </span>
  );
};

const RequestBox = () => {
  const [integrationRequestsEmail, setIntegrationRequestsEmail] = useState("");

  useEffect(() => {
    axios.get(`${API}/platform-emails/public`)
      .then(({ data }) => setIntegrationRequestsEmail(data.integration_requests_email || ""))
      .catch(() => setIntegrationRequestsEmail(""));
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-nova-ice p-5" data-testid="request-integration-box">
      <p className="text-sm font-bold text-nova-navy">Don't see your tool?</p>
      <p className="mt-1 text-xs text-nova-slate leading-relaxed">We're always adding new integrations.</p>
      {integrationRequestsEmail && (
        <a
          href={`mailto:${integrationRequestsEmail}?subject=Integration%20request`}
          data-testid="request-integration-link"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-nova-blue hover:gap-2.5 transition-all duration-300"
        >
          Request an integration
          <ArrowRight size={14} strokeWidth={2} />
        </a>
      )}
    </div>
  );
};

const DetailView = ({ integration, onBack }) => {
  const i = integration;
  return (
    <div data-testid="integration-detail" className="max-w-2xl">
      <button
        type="button"
        onClick={onBack}
        data-testid="integration-detail-back"
        className="inline-flex items-center gap-2 text-sm font-semibold text-nova-blue hover:gap-3 transition-all duration-300 mb-8"
      >
        <ArrowLeft size={15} strokeWidth={2} />
        Back to all integrations
      </button>

      <div className="flex items-center gap-4 flex-wrap">
        <span className="w-16 h-16 rounded-2xl border border-slate-200 bg-white shadow-soft flex items-center justify-center">
          <i.Logo />
        </span>
        <div className="min-w-0">
          <h3 className="text-2xl font-bold tracking-tight text-nova-navy">{i.name}</h3>
          <p className="text-sm text-nova-slate mt-0.5">{i.vendor}</p>
        </div>
        <span className="ml-auto"><StatusPill type={i.statusType} /></span>
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
        {[
          ["Category", categoryLabel(i.category)],
          ["Connection", i.authType],
          ["Vendor API", i.apiSupported ? "Documented API available" : "Under evaluation"],
          ["Nova connector", i.connectorStatus === "available" ? "Built and ready" : "Planned — not yet available"],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-4 px-5 py-3.5">
            <span className="text-xs font-medium text-nova-muted">{k}</span>
            <span className="text-sm font-semibold text-nova-navy text-right">{v}</span>
          </div>
        ))}
      </div>

      <p className="mt-6 text-sm text-nova-slate leading-relaxed">
        Nova can use supported {i.name} APIs to work with data such as{" "}
        {i.capabilities.slice(0, 3).join(", ").toLowerCase()} and more, where
        authorized by your business.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {i.capabilities.map((c) => (
          <span key={c} className="rounded-full border border-nova-bluelight bg-nova-mist px-3.5 py-1.5 text-xs font-semibold text-nova-blue">
            {c}
          </span>
        ))}
      </div>
    </div>
  );
};

export const IntegrationExplorer = ({ open, selectedId, onClose, onSelect }) => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = useMemo(() => {
    let list = INTEGRATIONS;
    if (category !== "all") list = list.filter((i) => i.category === category);
    return searchIntegrations(list, query);
  }, [category, query]);

  useEffect(() => setExpanded(false), [category, query]);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    searchRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const focusables = Array.from(
        panel.querySelectorAll("button, a[href], input, [tabindex]:not([tabindex='-1'])")
      ).filter((el) => !el.disabled && el.getClientRects().length > 0);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.__lenis?.start();
    };
  }, [open, onClose]);

  if (!open) return null;

  const selected = selectedId ? INTEGRATIONS.find((i) => i.id === selectedId) : null;
  const visible = expanded ? filtered : filtered.slice(0, 9);

  return (
    <div className="fixed inset-0 z-[80]" data-testid="integration-explorer">
      <motion.div
        data-testid="explorer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        className="absolute inset-0 bg-nova-navy/40 backdrop-blur-sm"
      />
      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="integrations-explorer-title"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-y-0 right-0 w-full sm:w-[88%] lg:w-[72%] max-w-[1100px] bg-white shadow-[-24px_0_80px_rgb(10,25,47,0.25)] flex flex-col"
      >
        <div className="flex items-start justify-between gap-6 px-6 sm:px-10 pt-8 sm:pt-10">
          <div>
            <h2 id="integrations-explorer-title" className="text-3xl font-bold tracking-tighter text-nova-navy">
              All Integrations
            </h2>
            <p className="mt-1.5 text-sm text-nova-slate">
              Connect Nova to the tools your business already uses.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="explorer-close-btn"
            aria-label="Close integrations panel"
            className="w-10 h-10 rounded-full border border-slate-200 bg-white flex items-center justify-center text-nova-navy hover:bg-nova-mist hover:border-nova-bluelight transition-colors duration-300 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 sm:px-10 mt-6">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-nova-muted" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search integrations..."
              data-testid="explorer-search-input"
              aria-label="Search integrations"
              className="w-full rounded-full border border-slate-200 bg-nova-ice/60 pl-11 pr-4 py-3 text-sm text-nova-navy placeholder:text-nova-muted focus:outline-none focus:border-nova-blue focus:ring-2 focus:ring-nova-blue/15 transition"
            />
          </div>
        </div>

        <div className="lg:hidden px-6 sm:px-10 mt-5 overflow-x-auto pb-1" data-testid="explorer-category-chips">
          <div className="flex gap-2 w-max">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { setCategory(c.id); onSelect(null); }}
                data-testid={`explorer-cat-${c.id}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold border transition-colors duration-300 ${
                  category === c.id
                    ? "bg-nova-blue text-white border-nova-blue"
                    : "bg-white text-nova-slate border-slate-200 hover:border-nova-bluelight"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 flex min-h-0 mt-6">
          <aside className="hidden lg:flex flex-col w-60 shrink-0 border-r border-slate-100 pl-10 pr-5 pb-8 overflow-y-auto" data-lenis-prevent>
            <nav className="flex flex-col gap-1" data-testid="explorer-category-nav">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { setCategory(c.id); onSelect(null); }}
                  data-testid={`explorer-nav-${c.id}`}
                  className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-left transition-colors duration-300 ${
                    category === c.id
                      ? "bg-nova-mist text-nova-blue font-semibold"
                      : "text-nova-slate hover:bg-nova-ice"
                  }`}
                >
                  <c.icon size={15} strokeWidth={1.7} />
                  {c.id === "all" ? "All Integrations" : c.label}
                </button>
              ))}
            </nav>
            <div className="mt-auto pt-8">
              <RequestBox />
            </div>
          </aside>

          <div className="flex-1 overflow-y-auto px-6 sm:px-10 pb-10" data-lenis-prevent>
            {selected ? (
              <DetailView integration={selected} onBack={() => onSelect(null)} />
            ) : (
              <>
                {filtered.length === 0 ? (
                  <div className="py-16 text-center" data-testid="explorer-empty">
                    <p className="text-sm font-semibold text-nova-navy">No integrations found</p>
                    <p className="mt-1 text-xs text-nova-muted">Try a different search or category.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="explorer-grid">
                    {visible.map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        onClick={() => onSelect(i.id)}
                        data-testid={`explorer-card-${i.id}`}
                        className="group text-left rounded-2xl border border-slate-200/90 bg-white p-5 transition-[transform,border-color,box-shadow] duration-300 ease-out hover:-translate-y-1 hover:border-nova-bluelight hover:shadow-[0_14px_34px_-10px_rgb(10,25,47,0.15)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563FF]"
                      >
                        <div className="flex items-center gap-3">
                          <span className="shrink-0 flex items-center justify-center"><i.Logo /></span>
                          <p className="text-sm font-bold text-nova-navy min-w-0 [overflow-wrap:anywhere]">{i.name}</p>
                        </div>
                        <p className="mt-2.5 text-xs text-nova-slate leading-relaxed [overflow-wrap:anywhere]">{i.description}</p>
                        <div className="mt-4">
                          <StatusPill type={i.statusType} />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!expanded && filtered.length > 9 && (
                  <div className="mt-8 text-center">
                    <button
                      type="button"
                      onClick={() => setExpanded(true)}
                      data-testid="explorer-load-more"
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-nova-navy hover:border-nova-bluelight hover:bg-nova-mist transition-colors duration-300"
                    >
                      Load more integrations
                      <ChevronDown size={15} />
                    </button>
                  </div>
                )}

                <div className="mt-8 lg:hidden">
                  <RequestBox />
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};
